class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onViewportResize = this.updateViewportHeight.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');
    this.notification.scrollTop = 0;
    this.updateViewportHeight();
    window.addEventListener('resize', this.onViewportResize);
    requestAnimationFrame(this.onViewportResize);

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.updateViewportHeight();
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);
    window.removeEventListener('resize', this.onViewportResize);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    const sections = parsedState.sections || {};
    const updates = this.getSectionsToRender().map((section) => ({
      target: document.getElementById(section.id),
      html: this.getSectionInnerHTML(sections[section.id], section.selector),
    }));
    const summary = document.getElementById('unimaglif-cart-notification-summary');
    const summaryHTML = this.getSectionInnerHTML(
      sections['cart-notification-product'],
      '#unimaglif-cart-notification-summary'
    );

    // The item was already added. If section rendering failed, show the actual
    // cart instead of stale totals; never retry the add-to-cart request here.
    if (!updates[0].target || updates[0].html === null || !summary || !summaryHTML?.trim()) {
      if (summary) summary.innerHTML = '';
      window.location.assign(routes.cart_url);
      return;
    }

    updates.forEach(({ target, html }) => {
      if (target && html !== null) target.innerHTML = html;
    });
    summary.innerHTML = summaryHTML;

    // This cart uses fetch; Nova only observes legacy jQuery AJAX events.
    // Refresh newly inserted Shopify money spans without changing totals.
    try {
      if (typeof window.baCurr?.refreshConversion === 'function') window.baCurr.refreshConversion();
    } catch (_) {
      // Converter failure must not prevent opening the cart or checkout.
    }

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (typeof html !== 'string' || !html.trim()) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML ?? null;
  }

  updateViewportHeight() {
    // The wrapper marks the notification's final top position, independent of
    // its slide-in transform. Keep its contents scrollable above the viewport edge.
    const top = this.notification.parentElement.getBoundingClientRect().top;
    const availableHeight = Math.max(0, window.innerHeight - Math.max(0, top) - 16);
    this.notification.style.setProperty('--unimaglif-notification-max-height', `${availableHeight}px`);
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
